/**
 * Fija una contraseña temporal a un usuario de administración. Keycloak le
 * pedirá cambiarla en el siguiente inicio de sesión.
 */

import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { setPlatformUserPassword } from '../services/platformUserService';
import type { PlatformUserRecord } from '../types/platformUser';

const i18nBase = 'resources.platform-users';

const PASSWORD_PATTERN =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{12,}$/;

interface FormValues {
  password: string;
  confirmPassword: string;
}

export interface PlatformUserPasswordDialogProps {
  record: PlatformUserRecord | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const PlatformUserPasswordDialog: React.FC<
  PlatformUserPasswordDialogProps
> = ({ record, onClose, onSuccess }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  useEffect(() => {
    if (record) reset({ password: '', confirmPassword: '' });
  }, [record, reset]);

  const password = watch('password');

  const onSubmit = async (values: FormValues): Promise<void> => {
    if (!record) return;
    setSubmitting(true);
    try {
      await setPlatformUserPassword(record.id, values.password);
      notify(`${i18nBase}.notifications.passwordSet`, { type: 'success' });
      onSuccess();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, 'ra.notification.http_error', translate),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={record !== null}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(`${i18nBase}.dialog.changePassword`, {
            email: record?.email ?? '',
          })}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Controller
              name="password"
              control={control}
              rules={{
                required: translate(`${i18nBase}.validation.passwordRequired`),
                pattern: {
                  value: PASSWORD_PATTERN,
                  message: translate(`${i18nBase}.validation.passwordPattern`),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  label={translate(`${i18nBase}.fields.newPassword`)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    translate(`${i18nBase}.help.temporaryPassword`)
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: translate(
                  `${i18nBase}.validation.confirmPasswordRequired`
                ),
                validate: (v) =>
                  v === password ||
                  translate(`${i18nBase}.validation.confirmPasswordMismatch`),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  type="password"
                  label={translate(`${i18nBase}.fields.confirmPassword`)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{ textTransform: 'none', width: { xs: '100%', sm: 140 }, height: 40 }}
            >
              {translate('ra.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !isValid}
              sx={{ textTransform: 'none', width: { xs: '100%', sm: 140 }, height: 40 }}
            >
              {translate('ra.action.save')}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PlatformUserPasswordDialog;
